import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { deleteBeaconFromCache } from '../queries/devices-cache';

type UseDeleteBeaconActionProps = {
    beaconId: string;
};

type UseDeleteBeaconActionData = {
    deleteBeacon: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Removing a beacon from a company.
 *
 * No `isAllowed`/`reason`: the gate is the beacons page's own
 * `canEditBeacons`, which hides the row action that calls this.
 */
export function useDeleteBeaconAction(
    props: UseDeleteBeaconActionProps
): UseDeleteBeaconActionData {
    const { beaconId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `DELETE /v3/devices/beacons/{beaconId}` declares gets a branch, bar
    // `BEACON_NOT_FOUND`, which this namespace has no string for yet and so
    // falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.devices.deleteBeacon(beaconId).then(() => {}),
        onSuccess: () => {
            deleteBeaconFromCache(queryClient, beaconId);

            UIToast.success(t('actions.delete_beacon.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        deleteBeacon: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
